import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsAPI, projectRepositoriesAPI } from '../../services/api';
import { projectPageKeys, invalidateProjectPageCaches } from '../../query/queryKeys';
import toast from '../../services/toast';

/**
 * Project detail page repos, dashboard, and members (TanStack Query).
 */
export function useProjectDetailQueries(orgId, streamId, projectId) {
  const queryClient = useQueryClient();
  const enabled = !!orgId && !!streamId && !!projectId;

  const reposQuery = useQuery({
    queryKey: projectPageKeys.repos(orgId, streamId, projectId),
    queryFn: () => projectRepositoriesAPI.list(orgId, streamId, projectId),
    enabled,
    staleTime: 30_000,
    select: (list) => (Array.isArray(list) ? list : []),
  });

  const dashboardQuery = useQuery({
    queryKey: projectPageKeys.dashboard(orgId, streamId, projectId),
    queryFn: () => projectsAPI.getDashboard(orgId, streamId, projectId),
    enabled,
    staleTime: 30_000,
  });

  const membersQuery = useQuery({
    queryKey: projectPageKeys.members(orgId, streamId, projectId),
    queryFn: () => projectsAPI.getMembers(orgId, streamId, projectId),
    enabled,
    staleTime: 30_000,
    select: (list) => (Array.isArray(list) ? list : []),
  });

  const repositories = useMemo(() => reposQuery.data ?? [], [reposQuery.data]);
  const projectMembers = useMemo(() => membersQuery.data ?? [], [membersQuery.data]);
  const dashboardStats = dashboardQuery.data ?? null;

  const invalidate = () => invalidateProjectPageCaches(queryClient, orgId, streamId, projectId);

  const removeMemberMutation = useMutation({
    mutationFn: (userId) => projectsAPI.removeMember(orgId, streamId, projectId, userId),
    onSuccess: () => {
      toast.success('Member removed from project');
      invalidate();
    },
    onError: (err: unknown) => toast.error((err as Error).message || 'Failed to remove member'),
  });

  return {
    repositories,
    dashboardStats,
    projectMembers,
    reposLoading: reposQuery.isPending,
    dashboardLoading: dashboardQuery.isPending,
    membersLoading: membersQuery.isPending,
    invalidate,
    refetchRepos: reposQuery.refetch,
    refetchDashboard: dashboardQuery.refetch,
    refetchMembers: membersQuery.refetch,
    removeMemberMutation,
  };
}
